import { Invitation } from '@playcastdotio/Messaging';
import { invitationStateV2, machineIdState, hostMatchmakingState, authState } from '../../stores/shared';
import { sendMessage } from '../realtime';
import { websocket, MatchmakingMessageType } from '@playcastdotio/Messaging';
import { sendMatchmakerHostConfig } from './matchmaker';
import { getIpcFunctions } from '../ipcInterface';
import { notifyPavilionModeDisabled } from './notifications';
import { serviceContainer } from '../../../services/ServiceContainer';

// Track the current soft kick timer ID so we can clear it if needed
let currentSoftKickTimerId: NodeJS.Timeout | null = null;

/**
 * Handle soft kick activation notification from API
 * This is only called when the host has no active invitation
 */
export const handleSoftKickActivation = async (): Promise<void> => {
  console.log('[SoftKick] Received soft kick activation notification from API - host has no active invitation');

  try {
    // Remove host from matchmaking by setting global matchmaking toggle to false
    hostMatchmakingState.updateState({
      ...hostMatchmakingState.currentState,
      globalMatchmakingToggle: false,
      matchmakeable: false,
    });

    // Send the updated config to the API
    sendMatchmakerHostConfig().catch((err) => {
      console.error('[SoftKick] Failed to send host config after soft kick:', err);
    });

    console.log('[SoftKick] Successfully removed host from matchmaking');

    // Perform complete shutdown process (same as executeSoftKick but without invitation)

    // Send analytics for soft kick completion
    await serviceContainer.analyticsManager.sendHostSoftKickedEvent();

    // Send notification that pavilion mode is disabled (triggers email)
    notifyPavilionModeDisabled();
    console.log(`[SoftKick] Pavilion disabled notification sent`);

    // Perform the same shutdown process as hard kick
    const ipcFuncs = getIpcFunctions();

    console.log(`[SoftKick] Stopping stream due to soft kick activation`);
    ipcFuncs.stopStream();

    // Safety check: Only proceed with full shutdown if in guest account mode
    if (hostMatchmakingState.currentState.guestAccount) {
      console.log('[SoftKick] Soft kick in guest mode - terminating pavilion and logging off');
      ipcFuncs.processTerminate('pavilion');
      ipcFuncs.systemLogOff('');
    } else {
      console.log('[SoftKick] Not in guest mode - graceful stream shutdown completed without system logoff');
    }

  } catch (error) {
    console.error('[SoftKick] Error handling soft kick activation:', error);
  }
};

/**
 * Check if soft kick has expired and handle session invalidation if needed
 * This is called when invitation state changes to check expiration
 */
export const checkSoftKickExpiration = (invitation: Invitation): void => {
  if (invitation.type !== 'cafe') {
    return;
  }
  console.log(`[SoftKick] Checking soft kick for invitation ${invitation.id}`);

  // Check if soft kick has an expiration time
  if (invitation.manifest.softKickExpirationTime) {
    // Clear any existing timer before starting a new one
    if (currentSoftKickTimerId) {
      console.log(`[SoftKick] Clearing existing timer for invitation ${invitation.id}`);
      clearTimeout(currentSoftKickTimerId);
      currentSoftKickTimerId = null;
    }

    // Calculate remaining time from the invitation's soft kick expiration time
    const currentTime = Date.now();
    const expirationTime = invitation.manifest.softKickExpirationTime;
    const remainingTime = Math.max(0, expirationTime - currentTime);

    console.log(`[SoftKick] Soft kick is active for invitation ${invitation.id}, expiration time: ${expirationTime}, current time: ${currentTime}, remaining time: ${remainingTime}ms`);

    // Set up a new timer to execute the soft kick after the remaining grace period
    currentSoftKickTimerId = setTimeout(() => {
      console.log(`[SoftKick] Grace period expired for invitation ${invitation.id}, executing soft kick`);
      currentSoftKickTimerId = null; // Clear the timer ID since it's been executed
      executeSoftKick(invitation);
    }, remainingTime);

    console.log(`[SoftKick] New timer set for invitation ${invitation.id} with ${remainingTime}ms remaining`);
  }
};

/**
 * Execute soft kick - perform complete guest mode shutdown
 */
const executeSoftKick = async (invitation: Invitation): Promise<void> => {
  if (invitation.type !== 'cafe') {
    return;
  }

  console.log(`[SoftKick] Executing soft kick for invitation ${invitation.id} - performing complete shutdown`);

  try {
    // Verify this host's machine is matched with this invitation
    if (invitation.manifest.machineId !== machineIdState.currentState) {
      console.error(`[SoftKick] Host machine ${machineIdState.currentState} is not matched with invitation ${invitation.id}`);
      return;
    }

    // Send analytics for soft kick completion
    await serviceContainer.analyticsManager.sendHostSoftKickedEvent();

    // Send notification that pavilion mode is disabled (triggers email)
    notifyPavilionModeDisabled();
    console.log(`[SoftKick] Pavilion disabled notification sent`);

    // Create the invalidation message
    const invalidationMessage = websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_INVALIDATE_SESSION, {
      invitationId: invitation.id,
      reason: 'Host soft kick completed',
    });

    console.log(`[SoftKick] Sending invalidation message:`, invalidationMessage);

    // Send the invalidation message - the invitation events listener will handle the cleanup
    try {
      sendMessage(invalidationMessage);
      console.log(`[SoftKick] Invalidation message sent successfully`);
    } catch (error) {
      console.error(`[SoftKick] Error sending invalidation message:`, error);
    }

    // Perform the same shutdown process as hard kick
    const ipcFuncs = getIpcFunctions();

    console.log(`[SoftKick] Stopping stream due to soft kick timeout`);
    ipcFuncs.stopStream();

    // Safety check: Only proceed with full shutdown if in guest account mode
    if (hostMatchmakingState.currentState.guestAccount) {
      console.log('[SoftKick] soft kicking in guest mode - terminating pavilion and logging off');
      ipcFuncs.processTerminate('pavilion');
      ipcFuncs.systemLogOff('');
    }

  } catch (error) {
    console.error(`[SoftKick] Error executing soft kick for invitation ${invitation.id}:`, error);
  }
};
